import type { StoreItem } from './types';
import type { LatLng } from './MapAdapter';
import { createMapAdapter } from './MapAdapterFactory';
import type { MapAdapter } from './MapAdapter';

export class StoreMap {
    private adapter: MapAdapter;
    private stores: StoreItem[];
    private globalPinUrl: string;
    private activeId: string;
    private onActivate: (id: string) => void;

    constructor(
        providerKey: string,
        stores: StoreItem[],
        globalPinUrl: string,
        onActivate: (id: string) => void,
    ) {
        this.adapter = createMapAdapter(providerKey);
        this.stores = stores;
        this.globalPinUrl = globalPinUrl;
        this.activeId = stores.length ? String(stores[0].id) : '';
        this.onActivate = onActivate;
    }

    async mount(scriptUrl: string, container: HTMLElement): Promise<void> {
        await this.loadScript(scriptUrl);
        const first = this.stores[0];
        await this.adapter.init({
            container,
            center: first ? { lat: first.lat, lng: first.lng } : { lat: 37.5665, lng: 126.978 },
            zoom: 14,
        });

        for (const store of this.stores) {
            const id = String(store.id);
            const pinUrl = store.pin_url || this.globalPinUrl;
            this.adapter.addMarker(id, {
                position: { lat: store.lat, lng: store.lng },
                title: store.name,
                pin: pinUrl ? { url: pinUrl, width: 54, height: 60 } : undefined,
            });
            this.adapter.onMarkerClick(id, () => this.activateStore(id, true));
        }

        if (this.activeId) {
            this.setActiveMarker(this.activeId);
            this.adapter.setRipple(this.activeId, true);
        }
        this.fitVisible(this.stores);
    }

    activateStore(id: string, pan: boolean): void {
        const prev = this.activeId;
        this.activeId = id;

        if (prev && prev !== id) {
            this.adapter.setMarkerActive(prev, false);
            this.adapter.setRipple(prev, false);
        }
        this.setActiveMarker(id);
        this.adapter.setRipple(id, true);

        if (pan) {
            const store = this.stores.find((s) => String(s.id) === id);
            if (store) {
                this.adapter.panTo({ lat: store.lat, lng: store.lng });
                if (this.adapter.getZoom() < 15) this.adapter.setZoom(15);
            }
        }
        this.onActivate(id);
    }

    private setActiveMarker(id: string): void {
        this.adapter.setMarkerActive(id, true);
    }

    applyFilter(visibleIds: Set<string>): void {
        const prevActive = this.activeId;
        for (const store of this.stores) {
            const id = String(store.id);
            this.adapter.setMarkerVisible(id, visibleIds.has(id));
            if (!visibleIds.has(id)) {
                this.adapter.setMarkerActive(id, false);
                this.adapter.setRipple(id, false);
            }
        }
        if (!visibleIds.has(this.activeId)) {
            this.adapter.setRipple(prevActive, false);
            const first = this.stores.find((s) => visibleIds.has(String(s.id)));
            this.activeId = first ? String(first.id) : '';
        }
        if (this.activeId) {
            this.adapter.setMarkerActive(this.activeId, true);
            this.adapter.setRipple(this.activeId, true);
            this.onActivate(this.activeId);
        }
        this.fitVisible(this.stores.filter((s) => visibleIds.has(String(s.id))));
    }

    private fitVisible(items: StoreItem[]): void {
        const positions: LatLng[] = items.map((s) => ({ lat: s.lat, lng: s.lng }));
        this.adapter.fitBounds(positions);
    }

    private loadScript(url: string): Promise<void> {
        return new Promise<void>((resolve, reject) => {
            const script = document.createElement('script');
            script.src = url;
            script.async = true;
            script.defer = true;
            script.onload = () => resolve();
            script.onerror = () => reject(new Error('지도 스크립트 로드 실패: ' + url));
            document.head.appendChild(script);
        });
    }
}
